/**
 * World map state (S16).
 *
 * Holds the map catalog (`GET /world-map/world`) and the player's fog-of-war
 * discovery (`GET /world-map/discovery`). Both are server-owned; this module
 * derives only *layout* — where to draw a region — which the backend has no
 * opinion about.
 *
 * ─── Why region geometry is derived here ────────────────────────────────────
 * `WorldMapDef.tiles` gives each region a `tier`, `label`, `order` and `cells`
 * (a footprint COUNT), but no coordinates. PoIs do carry `gridX`/`gridY` on the
 * map's `gridW` x `gridH` grid. So a region's on-screen position is computed from
 * the positions of its own PoIs, and `cells` only influences its drawn radius.
 *
 * That is a rendering decision, deliberately kept out of the backend: the server
 * would otherwise own pixel layout, and world-map-locations.md's own note calls
 * `cells` "rendering-shape only, not consumed by discovery logic".
 *
 * Consequence worth knowing: a region with NO PoIs has no derivable position.
 * Those get placed on a deterministic fallback ring by `order`, so they still
 * appear and stay put between renders rather than jumping around.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { raidStateStore, selectRegion } from './raid-state';

export type PoiKind = 'map_resolved' | 'location';

export interface PoiEntry {
  id: string;
  key: string;
  kind: PoiKind;
  gridX: number;
  gridY: number;
  region: string;
  isTutorial: boolean;
  unlocksFeature: string | null;
  /** Derived server-side from revealedRegions — never stored. */
  discovered: boolean;
}

export interface MapRegionDef {
  tier: number;
  label: string;
  cells: number;
  order: number;
  /** Centre of the circle in grid units. Absent on rows older than the 26-zone map. */
  x?: number;
  y?: number;
}

interface MapCatalogResponse {
  key: string;
  baseImage: string;
  gridW: number;
  gridH: number;
  tiles: Record<string, MapRegionDef>;
  pois: PoiEntry[];
}

interface DiscoveryResponse {
  revealedRegions: string[];
  discoveredPois: string[];
  updatedAt: string;
  explore?: ExploreProgress;
}

/**
 * Raid time banked toward the next zone (2026-10-19): zones open by hours spent
 * in raids, 8 / 12 / 24 per zone by tier, Explore-focus raids counting 1.5x.
 */
export interface ExploreProgress {
  minutes: number;
  next: { region: string; tier: number; needMinutes: number } | null;
  exploreFocusMult: number;
}

/** A region with its derived draw geometry, in GRID units (not pixels). */
export interface RegionLayout {
  key: string;
  label: string;
  tier: number;
  order: number;
  cells: number;
  cx: number;
  cy: number;
  /** Drawn radius in grid units, scaled from `cells`. */
  r: number;
  revealed: boolean;
  poiCount: number;
  discoveredPoiCount: number;
  /** True when the position came from the fallback ring, not from real PoIs. */
  positionEstimated: boolean;
}

export interface WorldMapState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  gridW: number;
  gridH: number;
  baseImage?: string;
  regions: RegionLayout[];
  pois: PoiEntry[];
  revealedRegions: string[];
  /** Null until the discovery read lands, or from a server older than 2026-10-19. */
  explore: ExploreProgress | null;
  error: string | null;
}

const INITIAL_STATE: WorldMapState = {
  status: 'idle', gridW: 40, gridH: 30, regions: [], pois: [], revealedRegions: [], explore: null, error: null,
};

export const worldMapStore = createStore<WorldMapState>({ ...INITIAL_STATE });

/**
 * Derives region layout from PoI positions. Pure and exported so it's testable
 * without a network — the geometry is the only real logic in this module.
 */
export function layoutRegions(
  tiles: Record<string, MapRegionDef>,
  pois: PoiEntry[],
  revealed: string[],
  gridW: number,
  gridH: number,
): RegionLayout[] {
  const revealedSet = new Set(revealed);
  const entries = Object.entries(tiles);

  return entries
    .map(([key, def]) => {
      const own = pois.filter((p) => p.region === key);
      let cx: number;
      let cy: number;
      let positionEstimated = false;

      if (typeof def.x === 'number' && typeof def.y === 'number') {
        // The map says where its own zone is. Preferred over the PoI centroid:
        // a zone's circle should not drift when a site inside it is added.
        cx = def.x;
        cy = def.y;
      } else if (own.length > 0) {
        cx = own.reduce((s, p) => s + p.gridX, 0) / own.length;
        cy = own.reduce((s, p) => s + p.gridY, 0) / own.length;
      } else {
        // Deterministic fallback ring, keyed on `order` so it never jitters
        // between renders. A region with no PoIs has no real position to show.
        const angle = (def.order / Math.max(1, entries.length)) * Math.PI * 2;
        cx = gridW / 2 + Math.cos(angle) * gridW * 0.3;
        cy = gridH / 2 + Math.sin(angle) * gridH * 0.3;
        positionEstimated = true;
      }

      return {
        key,
        label: def.label,
        tier: def.tier,
        order: def.order,
        cells: def.cells,
        cx,
        cy,
        // sqrt so a 14-cell region isn't 2.3x the radius of a 6-cell one —
        // area should scale with cells, not radius. Scaled by grid width so a
        // zone covers the same share of the painted map when the grid grows
        // (it was authored for 40 columns; the 26-zone map uses 80). The 0.45 is
        // down from 0.9: Janeks asked for the sector circles smaller, and the names
        // now sit below them rather than inside.
        r: Math.max(0.9, Math.sqrt(def.cells) * 0.45) * (gridW / 40),
        revealed: revealedSet.has(key),
        poiCount: own.length,
        discoveredPoiCount: own.filter((p) => p.discovered).length,
        positionEstimated,
      };
    })
    .sort((a, b) => a.tier - b.tier || a.order - b.order);
}

export async function loadWorldMap(): Promise<void> {
  worldMapStore.set((s) => ({ ...s, status: 'loading', error: null }));
  try {
    // Discovery is per-player and changes when a raid concludes; the catalog is
    // static. Fetched together because a stale pairing would render fog against
    // the wrong region set.
    const [map, discovery] = await Promise.all([
      api.get<MapCatalogResponse>('/world-map/world'),
      api.get<DiscoveryResponse>('/world-map/discovery'),
    ]);

    const pois = map.pois ?? [];
    const regions = layoutRegions(map.tiles ?? {}, pois, discovery.revealedRegions ?? [], map.gridW, map.gridH);

    // Seed the launch target if the player hasn't picked one.
    //
    // This is what makes a launch possible AT ALL on a fresh account, and its
    // absence was a real bug: the map used to populate only this store while the
    // focus buttons read `raidStateStore.selectedRegion`, so clicking a focus hit
    // `if (!target) return` and did nothing at all.
    //
    // Defaults to the safest region (regions are tier-then-order sorted) rather
    // than the safest REVEALED one, because `PlayerDiscovery` starts empty and
    // raid start does not gate on discovery — preferring a revealed region would
    // leave a new account with nothing selectable.
    if (!raidStateStore.get().selectedRegion && regions.length > 0) {
      selectRegion(regions[0].key);
    }

    worldMapStore.set({
      status: 'ready',
      gridW: map.gridW,
      baseImage: map.baseImage,
      gridH: map.gridH,
      regions,
      pois,
      revealedRegions: discovery.revealedRegions ?? [],
      explore: discovery.explore ?? null,
      error: null,
    });
  } catch (err) {
    worldMapStore.set((s) => ({
      ...s,
      status: 'error',
      error: err instanceof Error ? err.message : 'Failed to load the world map',
    }));
  }
}

/** Re-fetch after a raid concludes — Explore steps write new discoveries. */
export async function refreshDiscovery(): Promise<void> {
  if (worldMapStore.get().status !== 'ready') return;
  await loadWorldMap();
}

export function resetWorldMap(): void {
  worldMapStore.set({ ...INITIAL_STATE });
}
